import React from 'react';
import { Loading, Modal, Select, Checkbox } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import clsx from 'clsx';
import { callAll } from 'Utils';
import useGetListTypes from 'Hooks/query-hooks/list/useGetListTypes';
import { shouldShowMerchants, shouldShowPanList, shouldShowRangeInListTypes } from 'Selectors';
import { HIDDEN_LIST_TYPES } from 'Common/enums';
import {
    ListNameInput,
    addListValidationSchema,
    NameInputType,
    DescriptionInput,
    DescriptionInputType
} from '../ListForm';
import styles from './AddListModal.css';

type Props<D> = Pick<React.ComponentProps<typeof Modal>, 'isOpen' | 'closeModal'> & {
    isLoading: boolean;
    resetError: () => void;
    handleSubmit: (values: D) => void;
    error?: any;
};

const AddListModal = ({ isOpen, closeModal, isLoading, handleSubmit, error, resetError }: Props<List>) => {
    const { t } = useTranslation();
    const listTypesQuery = useGetListTypes({ runQuery: isOpen });
    let listTypesQueryFiltered = listTypesQuery?.data || [];
    if (!shouldShowPanList()) {
        listTypesQueryFiltered = listTypesQueryFiltered.filter((e) => e.key !== 'PAN');
    }

    if (!shouldShowRangeInListTypes()) {
        listTypesQueryFiltered = listTypesQueryFiltered.filter((e) => e.systemListType !== HIDDEN_LIST_TYPES.RANGE);
    }

    const combinedClose = callAll(resetError, closeModal);

    function renderModalBody(formikProps: FormikProps<List>) {
        const { setFieldValue } = formikProps;
        if (listTypesQuery.isError) {
            return (
                <div className={clsx(styles.modalBody, styles.spinner)}>
                    <p>There was an error fetching list types</p>
                </div>
            );
        }

        if (listTypesQuery.isLoading) {
            return (
                <div className={clsx(styles.modalBody, styles.spinner)}>
                    <Loading />
                </div>
            );
        }
        if (listTypesQuery.data) {
            return (
                <>
                    <ListNameInput
                        className={styles.listName}
                        error={error}
                        formikProps={(formikProps as unknown) as NameInputType}
                        resetError={resetError}
                    />
                    <Field name='listType'>
                        {({ field }: FieldProps) => {
                            return (
                                <Select
                                    dataTestId={`${field.name}-field`}
                                    className={styles.listType}
                                    label={t('List Type')}
                                    size='sm'
                                    placeholder={t('Select')}
                                    selectedItems={field.value ? [field.value] : []}
                                    handleClickCallback={([val]) => {
                                        setFieldValue(field.name, val);
                                    }}
                                >
                                    {listTypesQueryFiltered.map((o: any) => (
                                        <option key={o.key} value={o.key}>
                                            {t(`listTypes.${o.displayValue}`, o.displayValue)}
                                        </option>
                                    ))}
                                </Select>
                            );
                        }}
                    </Field>
                    <DescriptionInput formikProps={(formikProps as unknown) as DescriptionInputType} />
                    {shouldShowMerchants() && (
                        <Field name='merchantSpecific'>
                            {({ field }: FieldProps) => {
                                return (
                                    <Checkbox
                                        className={styles.checkbox}
                                        inline
                                        text={t('Include option to add Merchant-specific entries')}
                                        checked={field.value}
                                        onChange={(e: any) => {
                                            setFieldValue(field.name, e.target.checked);
                                        }}
                                    />
                                );
                            }}
                        </Field>
                    )}
                </>
            );
        }

        return null;
    }

    return (
        <Modal
            closeOnClickOutside={false}
            dataTestId='add-list-modal'
            containerClass={styles.addListModal}
            isOpen={isOpen}
            scrollLock
            closeModal={combinedClose}
            isSubmitting={isLoading}
        >
            <Modal.Title
                isSubmitting={isLoading}
                id='add-list-modal-title'
                title={t('Add a New List')}
                onClose={combinedClose}
            />
            <Formik
                enableReinitialize
                initialValues={{ name: '', listType: '', description: '', merchantSpecific: false }}
                onSubmit={(values) => {
                    handleSubmit(values);
                }}
                validationSchema={addListValidationSchema(t)}
            >
                {(formikProps: FormikProps<List>) => {
                    const { dirty, isValid } = formikProps;
                    const disableSave = isLoading || !dirty || !isValid;

                    return (
                        <>
                            <div className={styles.modalBody}>{renderModalBody(formikProps)}</div>
                            <Modal.Footer
                                id='add-list-modal-footer'
                                onClose={combinedClose}
                                disableOnCancel={isLoading}
                                disableOnAction={disableSave}
                                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Save')}
                                onAction={() => formikProps.submitForm()}
                            >
                                {isLoading && <span className={styles.saving}>{t('savingDots')}</span>}
                            </Modal.Footer>
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddListModal;
